import { Application, Container } from 'pixi.js';
import { resolveAppearance, type PlanetDef } from '@playnets/sim';
import { PlanetBodyView } from './planetBody';


/**
 * A single animated planet for the select screen. The whole point of the
 * screen is that you can tell the planets apart before you've read a word of
 * their stats, so the portrait runs the same body generator and the same
 * temperament motion the playfield does — not a still.
 */
export class PlanetPortrait {
  readonly app = new Application();
  private holder = new Container();
  private view: PlanetBodyView | null = null;
  private size = 240;
  private rotation = 0;
  private spin = 0;

  /**
   * Creates and owns its own canvas, which the caller mounts. Sharing a canvas
   * element with React is a trap: an effect that runs twice (StrictMode, or any
   * remount) leaves two Applications racing to initialise the same context, and
   * the discarded one tears the live canvas out of the DOM on destroy.
   */
  static async create(size: number): Promise<PlanetPortrait> {
    const p = new PlanetPortrait();
    p.size = size;
    await p.app.init({
      width: size,
      height: size,
      backgroundAlpha: 0,
      antialias: true,
      autoDensity: true,
      resolution: Math.min(globalThis.devicePixelRatio || 1, 2),
    });
    p.holder.position.set(size / 2, size / 2);
    p.app.stage.addChild(p.holder);
    // No grain overlay here. Over a transparent background it prints as a pale
    // box around the planet; the page's own paper texture carries the grain.

    p.app.ticker.add((ticker) => {
      const dt = ticker.deltaMS / 1000;
      p.rotation += p.spin * dt;
      p.view?.update(dt, p.rotation);
    });
    return p;
  }

  setPlanet(planet: PlanetDef, evolutions: readonly string[] = [], seed = 1): void {
    this.holder.removeChildren();
    this.view?.view.destroy({ children: true });

    const { body, palette } = resolveAppearance(planet, evolutions);
    // Portraits turn a little faster than the playfield so the character of
    // the spin — direction included — reads inside a couple of seconds.
    this.spin = planet.spin * 3.5;
    this.rotation = 0;
    this.view = new PlanetBodyView(
      body,
      palette,
      planet.temperament,
      this.size * 0.38,
      seed,
    );
    this.holder.addChild(this.view.view);
  }

  resize(size: number): void {
    this.size = size;
    this.app.renderer.resize(size, size);
    this.holder.position.set(size / 2, size / 2);
  }

  /** The canvas this portrait draws into. Mount it wherever it belongs. */
  get canvas(): HTMLCanvasElement {
    return this.app.canvas;
  }

  destroy(): void {
    this.app.destroy({ removeView: true }, { children: true });
  }
}
